import { Component, OnInit, signal, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

import {
  StockViewComponent,
  StockViewData,
} from './view/stock-view.component';
import { TransferItem } from './transfer.config';
import { TransferService } from './transfer.service';
import { FormField } from '@shared/components/add-form/add-form.component';

@Component({
  selector: 'app-transfer-view',
  standalone: true,
  imports: [CommonModule, StockViewComponent, TranslateModule],
  providers: [MessageService],
  template: `
    <app-stock-view
      [viewData]="viewData()"
      [loading]="loading()"
      [pageTitle]="'virtual_warehouse_transfer.form.view.title'"
      [formFields]="formFields()"
      [formData]="formData()"
      (backClick)="onBackClick()"
    ></app-stock-view>
  `,
})
export class TransferViewComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly transferService = inject(TransferService);
  private readonly translateService = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();

  // Data signals
  viewData = signal<StockViewData | null>(null);
  loading = signal(false);
  formFields = signal<FormField[]>([]);
  formData = signal<any>({});

  ngOnInit(): void {
    this.loadViewData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Navigation handlers
  onBackClick(): void {
    this.router.navigate(['/stock/transfer/virtual']);
  }

  private loadViewData(): void {
    this.loading.set(true);

    const id = this.route.snapshot.queryParamMap.get('id');

    if (!id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Invalid view parameters',
      });
      this.router.navigate(['/stock/transfer/virtual']);
      return;
    }

    this.loadTransferData(id);
  }

  private loadTransferData(id: string): void {
    this.transferService.getTransferById(id).subscribe({
      next: (data) => {
        if (data) {
          this.viewData.set({ type: 'transfer', data });
          this.configureTransferForm(data);
          this.loading.set(false);
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: 'Not Found',
            detail: 'Transfer record not found',
          });
          this.router.navigate(['/stock/transfer/virtual']);
        }
      },
      error: (error) => {
        console.error('Error loading virtual transfer data:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load transfer data',
        });
        this.loading.set(false);
        this.router.navigate(['/stock/transfer/virtual']);
      },
    });
  }

  private configureTransferForm(data: TransferItem): void {
    const fields: FormField[] = [
      // Basic Information Section
      {
        label: 'virtual_warehouse_transfer.form.fields.transfer_order_number',
        controlName: 'transferOrderNumber',
        type: 'text',
        section: 'virtual_warehouse_transfer.form.sections.basic_information',
        readonly: true,
        placeholder: 'virtual_warehouse_transfer.form.placeholders.enter_transfer_quantity',
      },
      {
        label: 'virtual_warehouse_transfer.form.fields.source_virtual_warehouse',
        controlName: 'sourceWarehouse',
        type: 'text',
        section: 'virtual_warehouse_transfer.form.sections.basic_information',
        readonly: true,
        placeholder: 'virtual_warehouse_transfer.form.placeholders.select_source_warehouse',
      },
      {
        label: 'virtual_warehouse_transfer.form.fields.destination_virtual_warehouse',
        controlName: 'destinationWarehouse',
        type: 'text',
        section: 'virtual_warehouse_transfer.form.sections.basic_information',
        readonly: true,
        placeholder: 'virtual_warehouse_transfer.form.placeholders.select_destination_warehouse',
      },
      {
        label: 'virtual_warehouse_transfer.form.fields.transfer_quantity',
        controlName: 'transferQuantity',
        type: 'number',
        section: 'virtual_warehouse_transfer.form.sections.basic_information',
        readonly: true,
        placeholder: 'virtual_warehouse_transfer.form.placeholders.enter_transfer_quantity',
      },
      {
        label: 'virtual_warehouse_transfer.form.fields.created_by',
        controlName: 'createdBy',
        type: 'text',
        section: 'virtual_warehouse_transfer.form.sections.basic_information',
        readonly: true,
        placeholder: 'virtual_warehouse_transfer.form.fields.created_by',
      },
      {
        label: 'virtual_warehouse_transfer.form.fields.creation_time',
        controlName: 'createdAt',
        type: 'text',
        section: 'virtual_warehouse_transfer.form.sections.basic_information',
        readonly: true,
        placeholder: 'virtual_warehouse_transfer.form.fields.creation_time',
      },
      {
        label: 'virtual_warehouse_transfer.form.fields.remarks',
        controlName: 'remark',
        type: 'text',
        section: 'virtual_warehouse_transfer.form.sections.basic_information',
        readonly: true,
        placeholder: 'virtual_warehouse_transfer.form.placeholders.enter_remarks',
      },
      {
        label: 'virtual_warehouse_transfer.form.fields.status',
        controlName: 'status',
        type: 'text',
        section: 'virtual_warehouse_transfer.form.sections.basic_information',
        readonly: true,
        placeholder: 'virtual_warehouse_transfer.form.fields.status',
      },
    ];

    this.formFields.set(fields);
    this.formData.set(data);
  }
}
